import React, { useState, useMemo } from "react";
import { Shell, Header, Tab } from "./Shell";
import { ReqRow, MonthCalendar, PolicyTab } from "./Shared";
import ChangePassword from "./ChangePassword";
import { s } from "../lib/theme";
import { LEAVE_TYPES, NOTICE_DAYS, HOURS_PER_DAY, businessDays, noticeDays, todayStr } from "../lib/pto";
import { Requests, Attachments, errMsg } from "../lib/api";

export default function EmployeeView({ me, users, requests, refreshAll, onLogout, previewMode }) {
  const [eTab, setETab] = useState("home");
  const [showPwd, setShowPwd] = useState(false);

  const myReqs = useMemo(
    () => requests.filter((r) => r.user_id === me.id).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [requests, me.id]
  );

  const [type, setType] = useState("PTO_VACATION");
  const [start, setStart] = useState(todayStr());
  const [end, setEnd] = useState(todayStr());
  const [note, setNote] = useState("");
  const [hoursStr, setHoursStr] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const meta = LEAVE_TYPES[type];
  const bDays = businessDays(start, end);
  const defaultHours = bDays * HOURS_PER_DAY;
  const overrideHours = hoursStr.trim() === "" ? null : Number(hoursStr);
  const hoursForDisplay =
    !meta.deducts ? 0 : (overrideHours !== null && !isNaN(overrideHours) ? overrideHours : defaultHours);
  const nd = noticeDays(start);
  const lateFlag = meta.noticeApplies && nd < NOTICE_DAYS;
  const overBalance = meta.deducts && hoursForDisplay > (me.balance_hours || 0);

  async function submit() {
    if (previewMode) {
      setMsg("Preview mode — requests aren't submitted from the admin preview.");
      return;
    }
    if (!start || !end || businessDays(start, end) === 0) {
      setErr("Pick a valid date range (weekdays only).");
      return;
    }
    if (overrideHours !== null && (isNaN(overrideHours) || overrideHours < 0)) {
      setErr("Hours must be zero or greater.");
      return;
    }
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const payload = { type, start, end, note: note.trim() };
      if (overrideHours !== null) payload.hours = overrideHours;
      const created = await Requests.create(payload);
      // Upload any staged attachments
      for (const f of pendingFiles) {
        try { await Attachments.upload(created.id, f); }
        catch (ex) { console.warn("attachment upload failed", ex); }
      }
      setNote("");
      setHoursStr("");
      setPendingFiles([]);
      const filesNote = pendingFiles.length ? ` (${pendingFiles.length} file${pendingFiles.length === 1 ? "" : "s"} attached)` : "";
      setMsg(`Request submitted${filesNote}. Dr. Kirk has been notified by email.`);
      await refreshAll();
    } catch (e) {
      setErr(errMsg(e, "Could not submit request."));
    } finally {
      setBusy(false);
    }
  }

  if (showPwd) {
    return (
      <ChangePassword
        user={me}
        forced={false}
        onSaved={async () => { setShowPwd(false); await refreshAll(); }}
        onCancel={() => setShowPwd(false)}
      />
    );
  }

  return (
    <Shell
      header={
        <Header
          title={`Welcome, ${me.name.split(" ")[0]}`}
          sub="Request time off and check your status"
          right={
            <div style={{ display: "flex", gap: 8 }}>
              {!previewMode && (
                <button data-testid="emp-change-pw-btn" style={s.btnGhost} onClick={() => setShowPwd(true)}>
                  Change Password
                </button>
              )}
              <button data-testid="emp-signout-btn" style={s.btnGhost} onClick={onLogout}>
                {previewMode ? "Exit Preview" : "Sign out"}
              </button>
            </div>
          }
        />
      }
    >
      <div style={s.tabs}>
        <Tab id="home" tab={eTab} setTab={setETab} testId="emp-tab-home">My Time Off</Tab>
        <Tab id="calendar" tab={eTab} setTab={setETab} testId="emp-tab-calendar">My Calendar</Tab>
        <Tab id="policy" tab={eTab} setTab={setETab} testId="emp-tab-policy">PTO Policy</Tab>
      </div>

      {eTab === "calendar" && (
        <div style={s.card}>
          <div style={s.cardTitle}>My Requests Calendar</div>
          <div style={{ ...s.muted, marginBottom: 12 }}>
            Green = approved · amber = pending · ★ gold = paid federal holiday (office closed). Denied requests are hidden.
          </div>
          <MonthCalendar requests={myReqs} users={users} scope="mine" />
        </div>
      )}

      {eTab === "policy" && <PolicyTab />}

      {eTab === "home" && (
        <>
          <div style={s.balanceCard} data-testid="emp-balance-card">
            <div>
              <div style={s.balLabel}>Your PTO Balance</div>
              <div style={s.balBig} data-testid="emp-balance-value">
                {me.balance_hours} <span style={s.balUnit}>hrs</span>
              </div>
              <div style={s.muted}>
                ≈ {(me.balance_hours / HOURS_PER_DAY).toFixed(1)} days · 8 hrs = 1 day
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: 12, color: "#fff", opacity: 0.85, lineHeight: 1.6 }}>
              Bereavement, jury duty, and unpaid<br />leave do not draw from your<br />PTO balance.
            </div>
          </div>

          <div style={s.card}>
            <div style={s.cardTitle}>New Request</div>
            <div style={s.row2}>
              <div>
                <label style={s.label}>Type of leave</label>
                <select data-testid="emp-type-select" style={s.input} value={type} onChange={(e) => setType(e.target.value)}>
                  {Object.entries(LEAVE_TYPES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={s.label}>Hours (leave blank = full days × 8)</label>
                <input
                  data-testid="emp-hours-input"
                  style={s.input}
                  type="number"
                  step="0.25"
                  min="0"
                  value={hoursStr}
                  onChange={(e) => setHoursStr(e.target.value)}
                  placeholder={meta.deducts ? `default ${defaultHours}` : "N/A"}
                  disabled={!meta.deducts}
                />
              </div>
            </div>
            <div style={s.row2}>
              <div>
                <label style={s.label}>Start</label>
                <input data-testid="emp-start-input" style={s.input} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div>
                <label style={s.label}>End</label>
                <input data-testid="emp-end-input" style={s.input} type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>

            <label style={s.label}>Note (optional)</label>
            <textarea
              data-testid="emp-note-input"
              style={{ ...s.input, height: 64, resize: "vertical" }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything Dr. Kirk should know"
            />

            <label style={s.label}>Attach files (optional)</label>
            <input
              data-testid="emp-files-input"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.heic,.heif,.webp,.gif,.ics,.txt,image/*,application/pdf,text/calendar"
              multiple
              onChange={(e) => setPendingFiles(Array.from(e.target.files || []))}
              style={{ fontSize: 12, marginBottom: 4 }}
            />
            <div style={{ fontSize: 11, color: "#9ca3af" }}>
              PDF, phone photo of paper form, calendar (.ics), up to 10 MB each (max 8 files).
              {pendingFiles.length > 0 && (
                <span style={{ color: "#0f5132", marginLeft: 6, fontWeight: 600 }}>
                  {pendingFiles.length} file{pendingFiles.length === 1 ? "" : "s"} ready to attach
                </span>
              )}
            </div>

            <div style={s.calcRow}>
              <span>{bDays} weekday{bDays === 1 ? "" : "s"}</span>
              {meta.deducts && <span>· {hoursForDisplay} hrs from PTO</span>}
              {!meta.deducts && <span>· does not use PTO</span>}
            </div>

            {lateFlag && (
              <div style={s.warn}>
                ⚠ This request is inside the {NOTICE_DAYS}-day notice window
                ({nd < 0 ? "past/started" : nd + " days out"}). It may be denied.
                Dr. Kirk will see it flagged as short notice.
              </div>
            )}
            {overBalance && (
              <div style={s.warn}>
                ⚠ This is more PTO ({hoursForDisplay} hrs) than your current balance ({me.balance_hours} hrs).
                You can still submit; Dr. Kirk will decide.
              </div>
            )}
            {msg && <div data-testid="emp-msg" style={s.info}>{msg}</div>}
            {err && <div data-testid="emp-err" style={s.error}>{err}</div>}
            <button data-testid="emp-submit-btn" disabled={busy} style={s.btnPrimary} onClick={submit}>
              {busy ? "Submitting…" : "Submit Request"}
            </button>
          </div>

          <div style={s.card}>
            <div style={s.cardTitle}>My Requests</div>
            {myReqs.length === 0 && <div style={s.muted}>No requests yet.</div>}
            {myReqs.map((r) => <ReqRow key={r.id} r={r} refreshAll={refreshAll} />)}
          </div>
        </>
      )}
    </Shell>
  );
}
